import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { of, ReplaySubject, Subject } from 'rxjs';

import { Centrifuge } from 'centrifuge';
import { BehaviorSubject, Observable } from 'rxjs';
import { ConfigService } from './config.service';
import { Subscription } from 'centrifuge/build/subscription';
import { filter, take, tap } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

interface CentrifugoConfig {
  token: string;
}

@Injectable({providedIn: 'root'})
export class CentrifugoService {
  private channels$: { [key: string]: Subject<any> } = {};
  private subscriptions: { [key: string]: Subscription } = {};
  private centrifuge: Centrifuge;
  private initializeEngineSubject: ReplaySubject<boolean>;
  private connectedSubject = new BehaviorSubject<boolean>(false);

  get connected$(): Observable<boolean> {
    return this.connectedSubject;
  }

  constructor(
    private httpClient: HttpClient,
    private configService: ConfigService,
    @Inject(PLATFORM_ID) private platformId: string,
  ) { }

  listen<T = any>(channel): Observable<T> {
    if (!isPlatformBrowser(this.platformId)) {
      console.error('Centrifugo is not available in ssr mode')
      return new Observable(observer => {});
    }

    if (channel in this.channels$) {
      return this.channels$[channel];
    }

    this.channels$[channel] = new Subject<T>();
    this.connect().subscribe(connected => {
      this.subscriptions[channel] = this.addEngineSubscription(channel);
    });

    return this.channels$[channel];
  }

  unsubscribe(channel): void {
    if (this.subscriptions[channel]) {
      this.subscriptions[channel].unsubscribe();
      this.centrifuge.removeSubscription(this.subscriptions[channel]);
      delete this.subscriptions[channel];
    }
    if (this.channels$[channel]) {
      this.channels$[channel].complete();
      delete this.channels$[channel];
    }
  }

  connect(): Observable<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      console.error('Centrifugo is not available in ssr mode');
      return new Observable<boolean>(observer => {});
    }

    if (!this.initializeEngineSubject) {
      this.initializeEngineSubject = new ReplaySubject(1);

      let centrifugoConfigUrl = this.configService.get('centrifugoConfigUrl');
      if (!centrifugoConfigUrl) {
        centrifugoConfigUrl = `${this.configService.get('apiUrl')}/api/v1/centrifugo_v2/`;
      }

      this.httpClient.get<CentrifugoConfig>(centrifugoConfigUrl)
        .subscribe(config => {
          this.initializeEngine({
            url: this.configService.get('centrifugoUrl'),
            token: config.token,
            transports: ['websocket']
          });
          this.centrifuge.on('connected', () => {
            this.initializeEngineSubject.next(true);
            this.connectedSubject.next(true);
          });
          this.centrifuge.on('disconnected', () => {
            this.connectedSubject.next(false);
          });
          this.centrifuge.connect();
        });
    }

    return this.initializeEngineSubject.pipe(
      filter(item => Boolean(item)),
      take(1),
      tap(() => {
        if (this.centrifuge && this.centrifuge.state === 'disconnected') {
          this.centrifuge.connect();
        }
      })
    );
  }

  publish(channel: string, message: any): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) {
      console.error('Centrifugo is not available in ssr mode');
      return new Observable<any>(observer => {});
    }

    return this.connect().pipe(
      tap(() => this.centrifuge.publish(channel, message))
    );
  }

  disconnect(): void {
    if (this.centrifuge) {
      this.centrifuge.disconnect();
    }
    this.initializeEngineSubject = undefined;
    for (const channel of Object.keys(this.channels$)) {
      this.unsubscribe(channel);
    }
    this.channels$ = {};
    this.subscriptions = {};
  }

  private initializeEngine(config: {
    url: string,
    token: string,
    transports: string[]
  }): void {
    this.centrifuge = new Centrifuge(config.url, {
      token: config.token,
      maxReconnectDelay: 3000,
      maxServerPingDelay: 3000,
      timeout: 1000,
    });
  }

  private addEngineSubscription(channel: string): Subscription {
    let sub = this.centrifuge.getSubscription(channel);
    if (!sub) {
      sub = this.centrifuge.newSubscription(channel);
      sub.on('publication', ctx => {
        this.resolveMessage(channel, ctx.data);
      });
    }
    sub.subscribe();
    return sub;
  }

  private resolveMessage(channel: string, message: any): void {
    if (!this.channels$[channel]) {
      return;
    }

    this.channels$[channel].next(message);
  }
}
